import React, { useRef, useEffect } from 'react';

export interface CheckboxProps {
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  testId?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  indeterminate = false,
  onChange,
  label,
  disabled = false,
  className = '',
  labelClassName = '',
  testId,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleChange = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const isActive = checked || indeterminate;

  return (
    <label
      className={`inline-flex w-fit items-center gap-1 select-none transition-all duration-200 rounded-lg outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#181A1B] has-[:focus-visible]:ring-offset-2 ${
        disabled ? 'opacity-38 cursor-not-allowed' : 'cursor-pointer'
      }`}
      data-testid={testId ?? 'checkbox-label'}
    >
      <span
        role="presentation"
        className={`relative w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors duration-200 ${
          disabled ? '' : 'hover:bg-[#E8EEF0]'
        }`}
      >
        {/* Hidden native input for accessibility */}
        <input
          ref={ref}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="sr-only"
          aria-label={label}
          data-testid={testId ? `${testId}-input` : 'checkbox-input'}
        />

        {/* Custom visual checkbox */}
        <div
          className={`w-[18px] h-[18px] rounded-[2px] border-2 rounded-[4px] flex items-center justify-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} shrink-0 transition-colors duration-150 ${
            isActive
              ? 'bg-[#2A4455] border-[#2A4455]'
              : `bg-white border-on-surface-variant ${className}`
          }`}
          data-testid="checkbox-visual"
        >
          {checked && (
            <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
              <path
                d="M1 4.5L4 7.5L11 1"
                stroke="#FFFFFF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          {indeterminate && !checked && (
            <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
              <path
                d="M1 1H9"
                stroke="#FFFFFF"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
        </div>
      </span>

      {/* Label */}
      {label && (
        <span
          className={`text-neutral-10 text-bodyMedium font-medium pointer-events-none ${labelClassName}`}
          data-testid="checkbox-label-text"
        >
          {label}
        </span>
      )}
    </label>
  );
};
